import { axiosRubyClient } from '@/util/axios/axiosClient';

export interface Assessment {
  id: number;
  external_id: string;
  user_id: number;
  user_name: string;
  user_email?: string;
  worker_id?: number | null;
  skill_id: number;
  skill_name: string;
  status:
    | 'pending'
    | 'recording'
    | 'transcribing'
    | 'scoring'
    | 'completed'
    | 'failed';
  result_status: 'failed' | 'for_review' | 'passed' | null;
  language: 'en' | 'es';
  reviewed_at: string | null;
  reviewed_by_id: number | null;
  reviewed_by_name: string | null;
  final_status: 'failed' | 'for_review' | 'passed' | 'invalid' | null;
  final_score: number | null;
  total_score: number | null;
  overall_feedback: string | null;
  overall_recommendation: string | null;
  override?: boolean | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentDetail extends Assessment {
  questions: AssessmentQuestion[];
  worker_id?: number | null;
  user_email?: string;
}

export interface AssessmentAnswer {
  id: number;
  transcript: string;
  score: number;
  ai_feedback: any;
  recording_url: string | null;
  category: string;
  created_at: string;
  updated_at: string;
}

export interface AssessmentQuestion {
  id: number;
  skill_id: number;
  ai_category_id: number;
  language: string;
  category: string;
  version: number;
  question_text: string;
  english_only: boolean;
  sort_index: number;
  audio_s3_key: string | null;
  answer: AssessmentAnswer | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentsFilters {
  include_reviewed?: boolean;
  reviewed?: boolean;
  include_pending?: boolean;
  status?: string;
  result_status?: string;
  user_id?: number;
  skill_id?: number;
  skill_name?: string;
  search?: string;
  page?: number;
  per_page?: number;
  sort?: 'created_at' | '-created_at';
}

export interface PaginationInfo {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AssessmentsIndexResponse {
  success: boolean;
  data: Assessment[];
  pagination: PaginationInfo;
}

export interface AssessmentDetailResponse {
  success: boolean;
  data: AssessmentDetail;
}

export interface ReviewParams {
  final_status: 'failed' | 'for_review' | 'passed' | 'invalid';
  notes?: string;
  final_score?: number;
  override?: boolean;
}

export interface ReviewResponse {
  success: boolean;
  data: Assessment;
}

export class AssessmentsAPI {
  /**
   * Get all assessments with filters
   */
  static async getAssessments(
    filters?: AssessmentsFilters,
  ): Promise<AssessmentsIndexResponse> {
    const params = new URLSearchParams();

    if (filters?.include_reviewed !== undefined)
      params.append('include_reviewed', filters.include_reviewed.toString());
    if (filters?.reviewed !== undefined)
      params.append('reviewed', filters.reviewed.toString());
    if (filters?.include_pending !== undefined)
      params.append('include_pending', filters.include_pending.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.result_status)
      params.append('result_status', filters.result_status);
    if (filters?.user_id) params.append('user_id', filters.user_id.toString());
    if (filters?.skill_id)
      params.append('skill_id', filters.skill_id.toString());
    if (filters?.skill_name) params.append('skill_name', filters.skill_name);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page)
      params.append('per_page', filters.per_page.toString());
    if (filters?.sort) params.append('sort', filters.sort);

    const response = await axiosRubyClient.get(
      `/api/admin/assessments?${params.toString()}`,
    );
    return response.data;
  }

  /**
   * Get a single assessment by external ID
   */
  static async getAssessment(
    externalId: string,
  ): Promise<AssessmentDetailResponse> {
    const response = await axiosRubyClient.get(
      `/api/admin/assessments/${externalId}`,
    );
    return response.data;
  }

  /**
   * Review an assessment
   */
  static async reviewAssessment(
    externalId: string,
    reviewData: ReviewParams,
  ): Promise<ReviewResponse> {
    const response = await axiosRubyClient.post(
      `/api/admin/assessments/${externalId}/review`,
      reviewData,
    );
    return response.data;
  }
}
